import { useMemo, useState } from "react";
import {
  Dropdown,
  Input,
  mergeClasses,
  Option,
  Tooltip,
} from "@fluentui/react-components";
import {
  AddRegular,
  ChevronDownRegular,
  EditRegular,
  InfoRegular,
} from "@fluentui/react-icons";
import { AdminListPage } from "components/adminList/AdminListPage";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { ITableColumn } from "models/ITableColumn";
import { getRoleDisplayName } from "apps/project-iq/utils/roleDisplayName";
import { formatDate } from "apps/project-iq/utils/workspaceOverviewUtils";
import { ReviewStatus } from "models/IProjectIQWorkspace";
import {
  NO_TEAM_MEMBERS_MESSAGE,
  NO_VALUE_PLACEHOLDER,
  REVIEWER_PLACEHOLDER,
  REVIEW_STATUS_OPTIONS,
} from "./TeamReviewTracker.constants";
import { useTeamReviewTrackerStyles } from "./TeamReviewTracker.styles";
import { IReviewRow, ITeamReviewTrackerProps } from "./TeamReviewTracker.types";

export const TeamReviewTracker = ({
  rows,
  canEdit,
  projectEndDate,
  onEntryChange,
}: ITeamReviewTrackerProps): JSX.Element => {
  const styles = useTeamReviewTrackerStyles();
  const colStyles = useAdminColumnStyles();
  const [editingReviewerRoleId, setEditingReviewerRoleId] = useState<
    string | null
  >(null);
  const [editingStatusRoleId, setEditingStatusRoleId] = useState<string | null>(
    null,
  );

  const dueDateText = projectEndDate
    ? formatDate(projectEndDate)
    : NO_VALUE_PLACEHOLDER;

  const columns = useMemo<ITableColumn<IReviewRow>[]>(
    () => [
      {
        columnId: "memberName",
        displayValue: "Team Member",
        isSortable: false,
        renderHeaderCell: () => "Team Member",
        compare: (a, b) => a.memberName.localeCompare(b.memberName),
        renderCell: (row) => row.memberName || NO_VALUE_PLACEHOLDER,
        className: colStyles.medium,
      },
      {
        columnId: "roleName",
        displayValue: "Role",
        isSortable: false,
        renderHeaderCell: () => "Role",
        compare: (a, b) => a.roleName.localeCompare(b.roleName),
        renderCell: (row) => (
          <span className={styles.cellLabel}>
            {getRoleDisplayName(row.roleName)}
          </span>
        ),
        className: colStyles.medium,
      },
      {
        columnId: "reviewer",
        displayValue: "Reviewer",
        isSortable: false,
        renderHeaderCell: () => "Reviewer",
        className: colStyles.medium,
        headerEndIcon: canEdit ? (
          <Tooltip
            content="Click a cell to add or edit the reviewer."
            relationship="label"
            positioning="above"
          >
            <InfoRegular className={styles.headerHintIcon} />
          </Tooltip>
        ) : undefined,
        compare: (a, b) => a.reviewer.localeCompare(b.reviewer),
        renderCell: (row) => {
          const rowIdentifier = `${row.memberName || "Unassigned"} — ${getRoleDisplayName(row.roleName)}`;
          if (!canEdit) return row.reviewer || NO_VALUE_PLACEHOLDER;
          if (editingReviewerRoleId === row.teamRoleId) {
            return (
              <Input
                size="small"
                className={styles.reviewerInput}
                value={row.reviewer}
                autoFocus
                onChange={(_e, data) =>
                  onEntryChange(row.teamRoleId, { reviewer: data.value })
                }
                onBlur={() => setEditingReviewerRoleId(null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") {
                    setEditingReviewerRoleId(null);
                  }
                }}
                aria-label={`Reviewer for ${rowIdentifier}`}
              />
            );
          }
          if (!row.reviewer) {
            return (
              <button
                type="button"
                className={mergeClasses(
                  styles.editableCell,
                  styles.addReviewerCell,
                )}
                onClick={() => setEditingReviewerRoleId(row.teamRoleId)}
                aria-label={`Add reviewer for ${rowIdentifier}`}
              >
                <span className={styles.cellLabel}>{REVIEWER_PLACEHOLDER}</span>
                <AddRegular
                  aria-hidden="true"
                  className={styles.trailingIcon}
                />
              </button>
            );
          }
          return (
            <button
              type="button"
              className={styles.editableCell}
              onClick={() => setEditingReviewerRoleId(row.teamRoleId)}
              aria-label={`Edit reviewer for ${rowIdentifier}`}
            >
              <span className={styles.cellLabel}>{row.reviewer}</span>
              <EditRegular aria-hidden="true" className={styles.trailingIcon} />
            </button>
          );
        },
      },
      {
        columnId: "status",
        displayValue: "Status",
        isSortable: false,
        renderHeaderCell: () => "Status",
        className: colStyles.medium,
        headerEndIcon: canEdit ? (
          <Tooltip
            content="Click a cell to update the review status."
            relationship="label"
            positioning="above"
          >
            <InfoRegular className={styles.headerHintIcon} />
          </Tooltip>
        ) : undefined,
        compare: (a, b) => a.status.localeCompare(b.status),
        renderCell: (row) => {
          const rowIdentifier = `${row.memberName || "Unassigned"} — ${getRoleDisplayName(row.roleName)}`;
          const statusLabel =
            REVIEW_STATUS_OPTIONS.find((option) => option.value === row.status)
              ?.label ?? row.status;
          if (!canEdit) return statusLabel;
          if (editingStatusRoleId === row.teamRoleId) {
            return (
              <Dropdown
                size="small"
                className={styles.statusDropdown}
                defaultOpen
                value={statusLabel}
                selectedOptions={[row.status]}
                onOptionSelect={(_e, data) => {
                  const nextStatus = data.optionValue as
                    ReviewStatus | undefined;
                  if (nextStatus) {
                    onEntryChange(row.teamRoleId, { status: nextStatus });
                  }
                  setEditingStatusRoleId(null);
                }}
                onOpenChange={(_e, data) => {
                  if (!data.open) setEditingStatusRoleId(null);
                }}
                aria-label={`Review status for ${rowIdentifier}`}
              >
                {REVIEW_STATUS_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Dropdown>
            );
          }
          return (
            <button
              type="button"
              className={styles.editableCell}
              onClick={() => setEditingStatusRoleId(row.teamRoleId)}
              aria-label={`Edit review status for ${rowIdentifier}`}
            >
              <span className={styles.cellLabel}>{statusLabel}</span>
              <ChevronDownRegular
                aria-hidden="true"
                className={styles.trailingIcon}
              />
            </button>
          );
        },
      },
      {
        columnId: "dueDate",
        displayValue: "Due Date",
        isSortable: false,
        renderHeaderCell: () => "Due Date",
        compare: () => 0,
        renderCell: () => dueDateText,
        className: colStyles.medium,
      },
      {
        columnId: "completedDate",
        displayValue: "Completed Date",
        isSortable: false,
        renderHeaderCell: () => "Completed Date",
        compare: (a, b) =>
          (a.completedDate ?? "").localeCompare(b.completedDate ?? ""),
        renderCell: (row) =>
          row.completedDate
            ? formatDate(row.completedDate)
            : NO_VALUE_PLACEHOLDER,
        className: colStyles.medium,
      },
    ],
    [
      canEdit,
      editingReviewerRoleId,
      editingStatusRoleId,
      dueDateText,
      onEntryChange,
      styles,
      colStyles,
    ],
  );

  return (
    <AdminListPage<IReviewRow>
      items={rows}
      columns={columns}
      getRowId={(row) => row.teamRoleId}
      noPadding
      emptyStateMessage={NO_TEAM_MEMBERS_MESSAGE}
    />
  );
};
